import { useEffect, useRef, useState } from 'react'
import { useAudioBibleStore } from '../stores/audioBibleStore'

export function VoicePickerSheet() {
  const voicePickerOpen = useAudioBibleStore((s) => s.voicePickerOpen)
  const voices = useAudioBibleStore((s) => s.voices)
  const currentVoiceId = useAudioBibleStore((s) => s.voiceId)
  const closeVoicePicker = useAudioBibleStore((s) => s.closeVoicePicker)
  const setVoice = useAudioBibleStore((s) => s.setVoice)

  const [sampling, setSampling] = useState<string | null>(null)
  const sampleAudioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    return () => {
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause()
        sampleAudioRef.current = null
      }
    }
  }, [])

  if (!voicePickerOpen) return null

  const playSample = (voiceId: string, url: string | null) => {
    if (sampleAudioRef.current) {
      sampleAudioRef.current.pause()
    }
    if (!url) return
    setSampling(voiceId)
    const audio = new Audio(url)
    sampleAudioRef.current = audio
    audio.addEventListener('ended', () => setSampling(null))
    audio.addEventListener('error', () => setSampling(null))
    void audio.play().catch(() => setSampling(null))
  }

  const selectVoice = (voiceId: string) => {
    if (sampleAudioRef.current) {
      sampleAudioRef.current.pause()
    }
    closeVoicePicker()
    void setVoice(voiceId)
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col justify-end"
      onClick={closeVoicePicker}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-elevated rounded-t-2xl p-4 pt-3 max-h-[70vh] overflow-y-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-surface rounded mx-auto mb-3" />
        <h3 className="text-text-primary font-bold text-base mb-1">Choose a voice</h3>
        <p className="text-text-muted text-xs mb-3 leading-relaxed">
          Tap ▶ to hear a sample. Your choice applies to this session. Set a default in Settings.
        </p>

        <div className="divide-y divide-surface">
          {voices.map((v) => {
            const isSelected = v.id === currentVoiceId
            const isSampling = sampling === v.id
            return (
              <div
                key={v.id}
                className={`flex items-center gap-3 py-2.5 -mx-4 px-4 ${isSelected ? 'bg-accent/10' : ''}`}
              >
                <button
                  type="button"
                  aria-label={`Play sample of ${v.name}`}
                  onClick={() => playSample(v.id, v.sample_url)}
                  disabled={!v.sample_url}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs shrink-0 ${
                    isSampling
                      ? 'bg-accent text-surface'
                      : isSelected
                      ? 'bg-accent text-surface'
                      : 'bg-surface text-text-primary'
                  } ${!v.sample_url ? 'opacity-50' : ''}`}
                >
                  {isSampling ? '■' : '▶'}
                </button>
                <button
                  type="button"
                  onClick={() => selectVoice(v.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-text-primary text-sm font-semibold">
                    {v.name} <span className="text-text-muted text-xs font-normal">({v.gender})</span>
                  </div>
                  <div className="text-text-muted text-xs truncate">{v.description}</div>
                </button>
                {isSelected && <span className="text-accent text-sm">✓</span>}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
